import React, { useEffect, useMemo, useState } from "react";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import { CustomButton, CustomDropdown } from "../../../../components";
import { useSelector } from "react-redux";
import styles from "../index.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { useLocation, useNavigate } from "react-router-dom";
import PopupModal from "../../../../components/PopupModal/PopupModal";

function SyllabusView() {
  const user = TokenService.getUser();
  const userId = user?.id || 0;
  const clientData = user?.client_data[0] || [];
  const school_id = clientData?.school_id || "";
  // const academic_year = clientData?.academic_year || "";
  const academic_id = clientData?.academic_year_id || "";
  const location = useLocation();
  const navigate = useNavigate();
  const { CLASSID, SYLLABUSID } = location.state || {};
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const editTableDate = data[0];
  const [examData, setExamData] = useState([]);
  const [formData, setFormData] = useState({ subject: "" });
  const { translations } = useSelector((state) => state.language);

  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });

  useEffect(() => {
    fetchSubjectList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SYLLABUSID]);

  const fetchSubjectList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/school/viewSyllabus", {
        id: SYLLABUSID,
      });

      if (response?.data?.status) {
        const Data = response.data.data;

        const normalizedData = (Array.isArray(Data) ? Data : [Data]).map(
          (row) => ({
            ...row,
            exam_plan: row.exam_plan ? Number(row.exam_plan) : "",
          }),
        );
        setFormData({
          subject: Data.subject_name,
        });
        setData(normalizedData);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching subject list", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Helper: keep digits only, cap at 4
  const sanitizeNumeric = (value) =>
    String(value ?? "")
      .replace(/\D/g, "")
      .slice(0, 4);

  // ✅ Helper: check if value is empty
  const isEmpty = (val) =>
    val === null || val === undefined || String(val).trim() === "";

  const columns = useMemo(
    () => [
      {
        accessorKey: "chapter",
        header: "Chapter",
        Cell: ({ row }) => (
          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={row.original.chapter || ""}
            onChange={(e) =>
              handleCellChange(
                row.index,
                "chapter",
                sanitizeNumeric(e.target.value),
              )
            }
            className={styles.underlineInput}
          />
        ),
      },
      {
        accessorKey: "units",
        header: "Units",
        Cell: ({ row }) => (
          <input
            value={row.original.units || ""}
            onChange={(e) =>
              handleCellChange(row.index, "units", e.target.value)
            }
            className={styles.underlineInput}
          />
        ),
      },
      {
        accessorKey: "short_description",
        header: "Short Description",
        Cell: ({ row }) => (
          <input
            value={row.original.short_description || ""}
            onChange={(e) =>
              handleCellChange(row.index, "short_description", e.target.value)
            }
            className={styles.underlineInput}
          />
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        Cell: ({ row }) => (
          <textarea
            value={row.original.description || ""}
            onChange={(e) =>
              handleCellChange(row.index, "description", e.target.value)
            }
            className={styles.underlineTextarea}
          />
        ),
      },
      {
        accessorKey: "page_from",
        header: "Page From",
        Cell: ({ row }) => (
          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={row.original.page_from || ""}
            onChange={(e) =>
              handleCellChange(
                row.index,
                "page_from",
                sanitizeNumeric(e.target.value),
              )
            }
            className={styles.underlineInput}
          />
        ),
      },
      {
        accessorKey: "page_to",
        header: "Page To",
        Cell: ({ row }) => (
          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={row.original.page_to || ""}
            onChange={(e) =>
              handleCellChange(
                row.index,
                "page_to",
                sanitizeNumeric(e.target.value),
              )
            }
            className={styles.underlineInput}
          />
        ),
      },
      {
        accessorKey: "exam_plan",
        header: "Exam Type",
        Cell: ({ row }) => (
          <select
            value={row.original.exam_plan || ""}
            onChange={(e) =>
              handleCellChange(row.index, "exam_plan", Number(e.target.value))
            }
            className={styles.underlineSelect}
          >
            <option value="">Select Examtype</option>

            {examData.map((exam) => (
              <option key={exam.value} value={exam.value}>
                {exam.label}
              </option>
            ))}
          </select>
        ),
      },
    ],
    [examData],
  );

  useEffect(() => {
    fetchExamList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExamList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/exam/examConfig/list", {
        school_id: school_id,
        academic_year: academic_id,
      });

      if (response?.data) {
        const exmaData = response.data.data;
        const data = exmaData.map((item) => ({
          label: item.exam_type,
          value: item.id,
        }));

        setExamData(data);
      } else {
        setExamData([]);
      }
    } catch (error) {
      console.error("Error fetching subject list", error);
      setExamData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCellChange = (rowIndex, key, value) => {
    setData((prev) =>
      prev.map((row, index) =>
        index === rowIndex ? { ...row, [key]: value } : row,
      ),
    );
  };

  // ✅ Validate the editable row before sending to API
  const validateRow = (row) => {
    if (!row) return "No data to update";

    const fields = [
      { key: "chapter", label: "Chapter" },
      { key: "units", label: "Units" },
      { key: "short_description", label: "Short Description" },
      { key: "description", label: "Description" },
      { key: "page_from", label: "Page From" },
      { key: "page_to", label: "Page To" },
      { key: "exam_plan", label: "Exam Type" },
    ];

    const missing = fields
      .filter((f) => isEmpty(row[f.key]))
      .map((f) => f.label);

    if (missing.length > 0) {
      return `Please fill the following field(s): ${missing.join(", ")}`;
    }
    return "";
  };

  const handleUpdateSyllabus = async (e) => {
    e.preventDefault();

    // ✅ Validate first — block API if any field empty
    const errorMessage = validateRow(editTableDate);
    if (errorMessage) {
      setModalState({
        open: true,
        type: "error",
        message: errorMessage,
        redirectPath: "",
      });
      return;
    }

    const payload = {
      chapter: editTableDate.chapter,
      units: editTableDate.units,
      short_description: editTableDate.short_description,
      description: editTableDate.description,
      page_from: editTableDate.page_from,
      page_to: editTableDate.page_to,
      exam_plan: editTableDate.exam_plan || "",
      updated_by: userId,
    };

    try {
      const response = await AxiosInstance.put(
        `/school/updateSyllabus/${SYLLABUSID}`,
        payload,
      );
      if (response.data.status) {
        setModalState({
          open: true,
          type: "success",
          message: response.data?.message || "Syllabus updated successfully",
          redirectPath: {
            path: "/user/syllabusList",
            state: { ID: CLASSID },
          },
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response.data?.message || "Update failed",
          redirectPath: "",
        });
      }
    } catch (err) {
      console.error(err);
      setModalState({
        open: true,
        type: "error",
        message: "Something went wrong. Please try again.",
        redirectPath: "",
      });
    }
  };

  const handleDeleteSyllabus = async (e) => {
    e.preventDefault();
    const payload = {
      id: SYLLABUSID,
      school_id: school_id,
      deleted_by: userId,
    };
    try {
      const response = await AxiosInstance.post(
        `/school/deleteSyllabus`,
        payload,
      );
      if (response.data.status) {
        navigate("/user/syllabusList", {
          state: {
            ID: CLASSID,
          },
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response.data?.message || "Delete failed",
          redirectPath: "",
        });
      }
    } catch (err) {
      console.error(err);
      setModalState({
        open: true,
        type: "error",
        message: "Something went wrong. Please try again.",
        redirectPath: "",
      });
    }
  };

  useEffect(() => {
    if (!examData.length || !data.length) return;

    setData((prev) =>
      prev.map((row) => {
        if (row.exam_plan) return row;
        const matched = examData.find((e) => e.label === row.exam_plan_name);
        return {
          ...row,
          exam_plan: matched?.value || "",
        };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examData, data.length]);

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));

    if (type === "success" && redirectPath) {
      navigate(redirectPath.path, { state: redirectPath.state });
    }
  };

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.header}>
            <CustomDropdown
              label={translations.labels.subject}
              name="subject"
              disabled
              value={formData.subject}
              placeholder={translations.labels.subjectPlaceholder}
              required
            />
          </div>
        </div>
        {/* Table */}
        <CustomMRTTable
          columns={columns}
          data={data}
          loading={loading}
          rowCount={data.length}
          placeholder={translations.labels.searchButton}
        />
        <div className="d-flex justify-content-center gap-3 mt-4">
          <CustomButton
            onClick={handleUpdateSyllabus}
            type="button"
            variant="contained"
            fullWidth={false}
            style={{
              backgroundColor: "#ff7a18",
              borderColor: "#ff7a18",
              color: "#fff",
            }}
          >
            {translations.labels?.updateButton || "Update"}
          </CustomButton>
          <CustomButton
            onClick={handleDeleteSyllabus}
            type="button"
            variant="contained"
            fullWidth={false}
            style={{
              backgroundColor: "#ff7a18",
              borderColor: "#ff7a18",
              color: "#fff",
            }}
          >
            {translations.labels?.deleteButton || "Delete"}
          </CustomButton>
        </div>
      </div>
    </>
  );
}

export default SyllabusView;
